import { Updater } from '@tanstack/react-table';
import { VisibilityState } from '@tanstack/table-core';
import { useEffect, useState } from 'react';
import { UserTableConfig } from '../stores/UserConfigStore';
import { arraysEqual } from '../utils/arrayFunc';
import { useUserConfigContext } from './useUserConfigContext';

const getCurrentHiddenColumns = (tableConfigs: UserTableConfig[] | null, tableName: string) => {
  return tableConfigs
    ?.find((tc) => tc.table === tableName)
    ?.hiddenColumns.reduce((acc: { [key: string]: boolean }, currentValue) => {
      acc[currentValue] = false;
      return acc;
    }, {});
};

const getHiddenColumnsArray = (columnVisibility: VisibilityState): string[] => {
  return Object.entries(columnVisibility)
    .filter(([_, isVisible]) => !isVisible)
    .map(([columnName, _]) => columnName);
};

export function useHiddenColumns(tableName: string, initial?: VisibilityState) {
  const userConfig = useUserConfigContext();
  const tableConfigs = userConfig.tableConfigs;

  const hiddenColumns = getCurrentHiddenColumns(tableConfigs, tableName);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(hiddenColumns ?? initial ?? {});

  const saveColumnVisibility = (updaterOrValue: Updater<VisibilityState>) => {
    if (updaterOrValue) {
      setColumnVisibility(updaterOrValue as VisibilityState);
    }
  };

  useEffect(() => {
    const saveHiddenColumns = async () => {
      // Get current hidden columns from the state
      const currentHiddenColumns = getHiddenColumnsArray(columnVisibility);

      // Get saved hidden columns from user config
      const savedHiddenColumns = userConfig.tableConfigs?.find((tc) => tc.table === tableName)?.hiddenColumns ?? [];

      // Only save if they differ
      if (!arraysEqual(currentHiddenColumns, savedHiddenColumns)) {
        await userConfig.saveHiddenColumns(tableName, currentHiddenColumns);
      }
    };

    void saveHiddenColumns();
  }, [columnVisibility, tableName, userConfig]);

  return { columnVisibility, saveColumnVisibility };
}
